/**
 * The brand: name, line, colour, languages, features, the menu and legal links, as the apps show
 * them. One page, one save (the save bar): a saved change reaches the apps' screens at the next
 * publish, and their icon, launch colour and store name at the next app build. What only an app
 * build can change (store ids, the backend's address, typefaces) is shown, not edited.
 */

import { Fragment, useEffect, useMemo, useState } from "react";
import Button from "@web-ui/components/Button";
import Chip from "@web-ui/components/Chip";
import Field from "@web-ui/components/Field";
import Notice from "@web-ui/components/Notice";
import Select from "@web-ui/components/Select";
import Switch from "@web-ui/components/Switch";
import { DEFAULT_GROUND, deriveColorTokens } from "@web-ui/brand/color-tokens";
import { brand as brandApi, entities, type BrandManifest, type BrandView, type EntitySummary } from "@/api/admin";
import { ApiError, type AdminClient } from "@/api/client";
import { SaveBar } from "@/components/save-bar";
import { BrandPreview } from "./brand-preview";
import { dateTime } from "@/lib/format";

/** Languages the editor offers; a brand can only add one the apps have their words in. */
const LANGUAGES: Record<string, string> = { en: "English", nl: "Nederlands", de: "Deutsch", fr: "Français", es: "Español" };
const languageName = (code: string) => LANGUAGES[code] ?? code;

const FEATURES: Array<{ key: keyof Omit<BrandManifest["features"], "give">; label: string; hint: string }> = [
  { key: "profiles", label: "Profiles", hint: "Who's watching, with progress kept per person." },
  { key: "kidsMode", label: "Kids profiles", hint: "Profiles that only see what is marked for children." },
  { key: "downloads", label: "Downloads", hint: "Phones keep videos for watching offline." },
  { key: "live", label: "Live", hint: "Live events with countdowns and replays." },
  { key: "cast", label: "Cast and AirPlay", hint: "Phones send what they play to a TV." },
  { key: "search", label: "Search", hint: "A search screen on every device." },
];

type Edit = Pick<BrandManifest, "name" | "tagline" | "locales" | "colors" | "features" | "menu" | "legal">;
const editOf = (m: BrandManifest): Edit => structuredClone({ name: m.name, tagline: m.tagline ?? "", locales: m.locales, colors: m.colors, features: m.features, menu: m.menu, legal: m.legal });

/** The editable parts the backend reports as changed since the build, in words. */
const CHANGED_WORDS: Record<string, string> = { name: "name", tagline: "line", locales: "languages", colors: "colour", features: "features", menu: "menu", legal: "legal links" };

export function BrandSettings({ client, isOwner }: { client: AdminClient; isOwner: boolean }) {
  const [view, setView] = useState<BrandView | null>(null);
  const [edit, setEdit] = useState<Edit | null>(null);
  const [pages, setPages] = useState<EntitySummary[]>([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: "positive" | "critical" | "warning"; text: string; problems?: string[] } | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  const load = (fresh?: BrandView) => {
    const apply = (v: BrandView) => { setView(v); setEdit(editOf(v.manifest)); };
    if (fresh) return apply(fresh);
    void brandApi.get(client).then(apply).catch((e) => { if (e instanceof ApiError && e.code === "brand_not_configured") setUnavailable(true); else throw e; });
  };
  useEffect(() => {
    load();
    void entities.list(client, { type: "page" }).then((r) => setPages(r.entities.filter((p) => p.status !== "archived")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  const dirty = useMemo(() => !!view && !!edit && JSON.stringify(editOf(view.manifest)) !== JSON.stringify(edit), [view, edit]);

  if (unavailable) return <section className="card"><Notice tone="warning">The brand can't be edited on this deployment yet. Ask your ViewForm contact to finish setting it up.</Notice></section>;
  if (!view || !edit) return <section className="card" aria-busy="true"><p className="text-body-2 text-t-secondary">Loading the brand…</p></section>;

  const set = <K extends keyof Edit>(key: K, value: Edit[K]) => setEdit({ ...edit, [key]: value });
  const readOnly = !isOwner;

  const save = async () => {
    setMessage(null);
    setSaving(true);
    try {
      const saved = await brandApi.save(client, edit, view.revision);
      load(saved);
      setMessage({ tone: "positive", text: "Saved. Your apps show it after your next publish." });
    } catch (e) {
      if (e instanceof ApiError && e.code === "conflict") {
        load(e.body["current"] as BrandView);
        setMessage({ tone: "warning", text: "Someone else saved the brand while you were editing. Their version is shown; make your change again." });
      } else if (e instanceof ApiError && e.code === "invalid_brand") setMessage({ tone: "critical", text: "Not saved. Please fix:", problems: e.body["problems"] as string[] });
      else setMessage({ tone: "critical", text: "The brand was not saved. Try again." });
    } finally {
      setSaving(false);
    }
  };

  const changed = view.changed.map((k) => CHANGED_WORDS[k] ?? k);

  return (
    <div className="flex flex-col gap-3">
      {readOnly ? <Notice tone="info">Only owners can change the brand. You can see it here.</Notice> : null}
      {message ? (
        <Notice tone={message.tone} onDismiss={() => setMessage(null)}>
          {message.text}
          {message.problems ? <ul className="mt-2 list-disc pl-5">{message.problems.map((p) => <li key={p}>{p}</li>)}</ul> : null}
        </Notice>
      ) : null}
      {changed.length ? (
        <Notice tone="info">
          You changed the {joinWords(changed)} since your apps were built. Screens show it after your next publish; the app icon, launch colour and store name change with your next app build.
        </Notice>
      ) : null}

      <div className="grid items-start gap-3 xl:grid-cols-[minmax(0,1fr)_minmax(22rem,28rem)]">
      <div className="flex min-w-0 flex-col gap-3">
      <Section title="Name and colour" description="How your apps introduce themselves.">
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Name" description="In the apps, their store listings and the sign-in screen." value={edit.name} disabled={readOnly} onChange={(e) => set("name", e.target.value)} />
          <Field label="Short line" optional description="Under the name on the sign-in screen and in store listings." value={edit.tagline ?? ""} disabled={readOnly} onChange={(e) => set("tagline", e.target.value)} />
        </div>
        <BrandColour accent={edit.colors.accent} disabled={readOnly} onChange={(accent) => set("colors", { ...edit.colors, accent })} />
      </Section>

      <Section title="Languages" description="The languages your apps speak. A viewer sees their device's language when it is here, otherwise the default.">
        <Languages locales={edit.locales} readOnly={readOnly} onChange={(locales) => set("locales", locales)} />
      </Section>

      <Section title="Features" description="What your apps include.">
        <div className="grid gap-3 md:grid-cols-2">
          {FEATURES.map((f) => (
            <FeatureSwitch key={f.key} label={f.label} hint={f.hint} on={edit.features[f.key]} disabled={readOnly}
              onChange={(on) => set("features", { ...edit.features, [f.key]: on })} />
          ))}
          <Giving url={edit.features.give?.url} disabled={readOnly}
            onChange={(url) => { const { give: _give, ...rest } = edit.features; set("features", url === null ? rest : { ...rest, give: { url } }); }} />
        </div>
      </Section>

      <MenuEditor menu={edit.menu} pages={pages} locales={edit.locales.supported} defaultLocale={edit.locales.default} readOnly={readOnly} onChange={(menu) => set("menu", menu)} />

      <Section title="Legal and support" description="Linked from the apps' settings and the store listings.">
        <div className="grid gap-4 md:grid-cols-3">
          <Field label="Privacy policy" placeholder="https://" value={edit.legal.privacyUrl} disabled={readOnly} onChange={(e) => set("legal", { ...edit.legal, privacyUrl: e.target.value })} />
          <Field label="Terms of use" placeholder="https://" value={edit.legal.termsUrl} disabled={readOnly} onChange={(e) => set("legal", { ...edit.legal, termsUrl: e.target.value })} />
          <Field label="Support email" type="email" value={edit.legal.supportEmail} disabled={readOnly} onChange={(e) => set("legal", { ...edit.legal, supportEmail: e.target.value })} />
        </div>
      </Section>

      <BuiltIn view={view} />
      </div>
      <div className="flex flex-col gap-3 xl:sticky xl:top-4">
        <BrandPreview name={edit.name} accent={edit.colors.accent} menu={edit.menu} locales={edit.locales.supported} defaultLocale={edit.locales.default} languageName={languageName} />
      </div>
      </div>

      {view.revision && view.updatedAt ? <p className="px-1 text-caption text-t-tertiary">Last saved {dateTime(view.updatedAt)}.</p> : null}
      {isOwner ? <SaveBar dirty={dirty} saving={saving} onSave={() => void save()} onDiscard={() => { setEdit(editOf(view.manifest)); setMessage(null); }} note="Your apps show it after your next publish." /> : null}
    </div>
  );
}

function Section({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <section className="card flex flex-col gap-4">
      <header className="flex flex-col gap-1 ps-card-inset">
        <h3 className="text-sub-title-1">{title}</h3>
        {description ? <p className="text-body-2 text-t-secondary">{description}</p> : null}
      </header>
      {children}
    </section>
  );
}

function FeatureSwitch({ label, hint, on, disabled, onChange, children, wide }: { label: string; hint: string; on: boolean; disabled: boolean; onChange: (on: boolean) => void; children?: React.ReactNode; wide?: boolean }) {
  return (
    <div className={`flex flex-col gap-3 rounded-2xl bg-b-surface1 p-4 ${wide ? "md:col-span-2" : ""}`}>
      <label className="flex items-start gap-3">
        <Switch checked={on} disabled={disabled} ariaLabel={label} onChange={onChange} />
        <span className="flex flex-col gap-0.5"><span className="text-body-2">{label}</span><span className="text-caption text-t-secondary">{hint}</span></span>
      </label>
      {children}
    </div>
  );
}

/** Giving: a switch, and when on the page it opens (a QR code on TV, a link on phones). */
function Giving({ url, disabled, onChange }: { url: string | undefined; disabled: boolean; onChange: (url: string | null) => void }) {
  const on = url !== undefined;
  return (
    <FeatureSwitch label="Giving" hint="A QR code on TV and a link on phones to your giving page." on={on} wide disabled={disabled} onChange={(next) => onChange(next ? "https://" : null)}>
      {on ? (
        <Field label="Giving page" placeholder="https://" value={url} disabled={disabled} error={url && !/^https:\/\/\S+\.\S+/.test(url) ? "Enter the page's full address, starting with https://" : undefined}
          onChange={(e) => onChange(e.target.value)} />
      ) : null}
    </FeatureSwitch>
  );
}

/** One brand colour, with what the apps make of it on their dark ground. */
/** The chosen languages as rows (the default first, marked), and the others to add. */
function Languages({ locales, readOnly, onChange }: { locales: Edit["locales"]; readOnly: boolean; onChange: (locales: Edit["locales"]) => void }) {
  const chosen = [locales.default, ...locales.supported.filter((l) => l !== locales.default)];
  const others = Object.keys(LANGUAGES).filter((l) => !chosen.includes(l));
  return (
    <div className="flex flex-col gap-3">
      <ul className="flex flex-col divide-y divide-s-subtle overflow-hidden rounded-2xl bg-b-surface1">
        {chosen.map((code) => {
          const isDefault = code === locales.default;
          return (
            <li key={code} className="flex min-h-14 flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2">
              <span className="text-body-2">{languageName(code)}</span>
              {isDefault ? <Chip tone="accent">Default</Chip> : null}
              <span className="grow" />
              {isDefault ? (
                <span className="text-caption text-t-secondary">Every tab name needs it</span>
              ) : !readOnly ? (
                <span className="flex gap-1">
                  <Button type="button" size="sm" variant="ghost" onClick={() => onChange({ ...locales, default: code })}>Make default</Button>
                  <Button type="button" size="sm" variant="ghost" icon="trash" isIconOnly aria-label={`Remove ${languageName(code)}`}
                    onClick={() => onChange({ ...locales, supported: locales.supported.filter((l) => l !== code) })} />
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
      {!readOnly && others.length ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-body-2 text-t-secondary">Add</span>
          {others.map((code) => (
            <Button key={code} type="button" size="sm" icon="plus" onClick={() => onChange({ ...locales, supported: [...locales.supported, code] })}>{languageName(code)}</Button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function BrandColour({ accent, disabled, onChange }: { accent: string; disabled: boolean; onChange: (accent: string) => void }) {
  const valid = /^#[0-9a-fA-F]{6}$/.test(accent);
  const tokens = deriveColorTokens({ accent: valid ? accent : null }, DEFAULT_GROUND);
  return (
    <div className="grid gap-4">
      <div className="flex max-w-md flex-col gap-2">
        <span className="text-body-2" id="brand-colour-label">Brand colour</span>
        <div className="flex items-center gap-3">
          <label className="relative size-14 shrink-0 cursor-pointer overflow-hidden rounded-2xl border border-s-subtle" style={{ background: valid ? accent : tokens.accent }}>
            <span className="sr-only">Pick the brand colour</span>
            <input type="color" className="absolute inset-0 size-full cursor-pointer opacity-0" value={valid ? accent.toLowerCase() : tokens.accent} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
          </label>
          <div className="grow">
            <Field aria-labelledby="brand-colour-label" value={accent} disabled={disabled} error={valid ? undefined : "A # and six characters from 0–9 and a–f"} onChange={(e) => onChange(e.target.value.trim())} />
          </div>
        </div>
        <p className="text-caption text-t-secondary">Buttons use it as it is; text and focus rings in it are shaded where needed so they stay readable. See it in the preview.</p>
      </div>
    </div>
  );
}

/** What only an app build changes: shown so nobody hunts for it, never edited here. */
function BuiltIn({ view }: { view: BrandView }) {
  const m = view.manifest;
  const rows: Array<[string, string]> = [
    ["App Store id", m.stores.apple?.bundleId ?? "Not on the App Store"],
    ["Google Play id", m.stores.google?.packageName ?? "Not on Google Play"],
    ["Typefaces", [m.fonts.display?.family, m.fonts.body?.family].filter(Boolean).join(" and ") || "The system's"],
  ];
  return (
    <details className="card group">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 ps-card-inset">
        <span className="flex flex-col gap-1">
          <span className="text-sub-title-1">Set when your apps are built</span>
          <span className="text-body-2 text-t-secondary">Changing these needs a new version of the apps. Ask your ViewForm contact.</span>
        </span>
        <span className="text-body-2 text-t-secondary group-open:hidden">Show</span>
        <span className="hidden text-body-2 text-t-secondary group-open:inline">Hide</span>
      </summary>
      <dl className="mt-4 grid gap-x-6 gap-y-2 text-body-2 md:grid-cols-[12rem_1fr]">
        {rows.map(([k, v]) => <Fragment key={k}><dt className="text-t-secondary">{k}</dt><dd>{v}</dd></Fragment>)}
      </dl>
    </details>
  );
}

function MenuEditor({ menu, pages, locales, defaultLocale, readOnly, onChange }: {
  menu: Edit["menu"]; pages: EntitySummary[]; locales: string[]; defaultLocale: string; readOnly: boolean; onChange: (menu: Edit["menu"]) => void;
}) {
  const options = pages.map((p, id) => ({ id, name: p.label || p.id }));
  const unused = pages.filter((p) => !menu.some((m) => m.pageId === p.id));
  const ordered = [defaultLocale, ...locales.filter((l) => l !== defaultLocale)];
  const move = (i: number, by: number) => { const next = [...menu]; const [m] = next.splice(i, 1); next.splice(i + by, 0, m!); onChange(next); };
  return (
    <Section title="Menu" description="The tabs of your app, in order: along the top on TV, along the bottom on phones. Each has a name and opens one of your screens; search and settings are added by the apps. A name left empty in another language uses the default one.">
      <div className="hidden items-center gap-2 px-3 text-caption text-t-secondary lg:flex" aria-hidden>
        <span className="w-8 shrink-0">Tab</span>
        {ordered.map((l) => <span key={l} className="min-w-0 flex-1 basis-32 truncate">Name in {languageName(l)}</span>)}
        <span className="w-40 shrink-0">Opens</span>
        <span className="w-[7.5rem] shrink-0" />
      </div>
      <ol className="flex flex-col gap-2">
        {menu.map((m, i) => {
          const missing = !pages.some((p) => p.id === m.pageId);
          const badge = <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-b-surface2 text-body-2 tabular-nums" aria-hidden>{i + 1}</span>;
          const actions = (
            <div className="flex shrink-0 gap-1">
              <Button type="button" size="sm" variant="ghost" icon="arrow-up" isIconOnly aria-label={`Move tab ${i + 1} up`} disabled={readOnly || i === 0} onClick={() => move(i, -1)} />
              <Button type="button" size="sm" variant="ghost" icon="arrow-down" isIconOnly aria-label={`Move tab ${i + 1} down`} disabled={readOnly || i === menu.length - 1} onClick={() => move(i, 1)} />
              <Button type="button" size="sm" variant="ghost" icon="trash" isIconOnly aria-label={`Remove tab ${i + 1}`} disabled={readOnly || menu.length === 1} onClick={() => onChange(menu.filter((_, j) => j !== i))} />
            </div>
          );
          return (
            <li key={`${m.pageId}-${i}`} className="flex flex-col gap-2 rounded-2xl bg-b-surface1 p-3">
              <div className="flex items-center gap-2 lg:hidden">{badge}<span className="grow text-sub-title-2">Tab {i + 1}</span>{actions}</div>
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-2">
                <span className="hidden lg:block">{badge}</span>
                {ordered.map((l) => (
                  <div key={l} className="flex min-w-0 flex-col gap-1 lg:flex-1 lg:basis-32">
                    <span className="text-caption text-t-secondary lg:hidden" aria-hidden>Name in {languageName(l)}{l === defaultLocale ? "" : " · optional"}</span>
                    <Field aria-label={`Tab ${i + 1} name in ${languageName(l)}`} placeholder={l === defaultLocale ? "Tab name" : m.label[defaultLocale] || "Tab name"} value={m.label[l] ?? ""} disabled={readOnly}
                      error={l === defaultLocale && !m.label[l]?.trim() ? `Needs a name in ${languageName(l)}` : undefined}
                      onChange={(e) => onChange(menu.map((x, j) => (j === i ? { ...x, label: { ...x.label, [l]: e.target.value } } : x)))} />
                  </div>
                ))}
                <div className="flex w-full flex-col gap-1 lg:w-40 lg:shrink-0">
                  <span className="text-caption text-t-secondary lg:hidden" aria-hidden>Opens</span>
                  <Select ariaLabel={`Tab ${i + 1} opens`} value={options.find((o) => pages[o.id]?.id === m.pageId) ?? { id: -1, name: "Choose a screen" }} options={options}
                    onChange={(o) => { if (!readOnly) onChange(menu.map((x, j) => (j === i ? { ...x, pageId: pages[o.id]!.id } : x))); }} />
                </div>
                <div className="hidden lg:block">{actions}</div>
              </div>
              {missing ? <Notice tone="warning">This tab opens a screen that doesn't exist, so it would be empty. Pick another screen, or add it in Your app.</Notice> : null}
            </li>
          );
        })}
      </ol>
      {!readOnly && unused.length ? (
        <div><Button type="button" size="sm" icon="plus" onClick={() => onChange([...menu, { pageId: unused[0]!.id, label: Object.fromEntries(locales.map((l) => [l, unused[0]!.label])) }])}>Add a tab</Button></div>
      ) : null}
    </Section>
  );
}

function joinWords(words: string[]): string {
  return words.length < 2 ? words.join("") : `${words.slice(0, -1).join(", ")} and ${words.at(-1)}`;
}
